import { apiClient } from './apiClient';

/**
 * User Profile Service
 * Handles user profile operations
 */

export type AccountType = 'creator' | 'individual' | 'buyer';

export interface UserProfile {
  id: string;
  email: string;
  emailVerified?: boolean;
  fullName: string;
  brand?: string; // Keep for backward compatibility
  brand_name?: string; // New field from backend
  username?: string;
  role: string;
  accountType?: AccountType;
  bio?: string;
  location?: string;
  phone?: string;
  profilePicture?: string;
  coverImage?: string;
  specialty?: string[];
  socialLinks?: {
    instagram?: string;
    twitter?: string;
    facebook?: string;
    tiktok?: string;
    website?: string;
    [key: string]: string | undefined;
  };
  verified?: boolean;
  createdAt?: string;
  updatedAt?: string;
  
  // New API fields (v1/users/me)
  isPremium?: boolean;
  planType?: 'free' | 'premium' | 'pro' | string;
  features?: {
    analytics?: boolean;
    messaging?: boolean;
    priorityListing?: boolean;
    featured?: boolean;
    verificationBadge?: boolean;
    /** Buyer wishlist server sync (FAVORITES_SERVER_SYNC). */
    wishlist_sync?: boolean;
    /** Show "Buyer" option on signup. */
    buyer_signup?: boolean;
    [key: string]: boolean | undefined;
  };
}

export interface UpdateProfileData {
  fullName?: string;
  brand?: string; // Keep for backward compatibility
  brand_name?: string; // New field from backend
  bio?: string;
  location?: string;
  phone?: string;
  specialty?: string[];
  /** Creator opt-in for global client opportunities. */
  openToOpportunities?: boolean;
  openToOpportunitiesAt?: string;
  socialLinks?: {
    instagram?: string;
    twitter?: string;
    facebook?: string;
    tiktok?: string;
    website?: string;
    [key: string]: string | undefined;
  };
}

export interface ResetPasswordData {
  email: string;
}

export const userService = {
  /**
   * Get current user profile
   * GET /users/me
   */
  getCurrentUser: async () => {
    return await apiClient.get<UserProfile>('/users/me');
  },

  /**
   * Update current user profile
   * PATCH /users/me
   */
  updateProfile: async (data: UpdateProfileData) => {
    // console.log(data, "data from update profile");
    return await apiClient.patch<UserProfile>('/users/me', data);
  },

  /**
   * Get user by ID (public view)
   * GET /users/:id
   */
  getUserById: async (userId: string) => {
    return await apiClient.get<UserProfile>(`/users/${userId}`);
  },

  /**
   * Reset password with email
   * POST /auth/reset-password
   */
  resetPassword: async (data: ResetPasswordData) => {
    return await apiClient.post('/auth/reset-password', data);
  },

  /**
   * Upload profile picture
   * POST /users/me/profile-picture
   */
  uploadProfilePicture: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return await apiClient.uploadFile<UserProfile>('/users/me/profile-picture', formData);
  },

  /**
   * Update profile picture
   * PATCH /users/me/profile-picture
   */
  updateProfilePicture: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    // Use uploadFile method for PATCH as well since it handles multipart data
    return await apiClient.uploadFile<UserProfile>('/users/me/profile-picture', formData);
  },

  /**
   * Upload cover image
   * POST /users/me/cover-image
   */
  uploadCoverImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    return await apiClient.uploadFile<UserProfile>('/users/me/cover-image', formData);
  },

  /**
   * Update cover image
   * PATCH /users/me/cover-image
   */
  updateCoverImage: async (imageFile: File) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    
    // Use uploadFile method for PATCH as well since it handles multipart data
    return await apiClient.uploadFile<UserProfile>('/users/me/cover-image', formData);
  },

  /**
   * Switch account type
   * PATCH /users/account-type
   */
  switchAccountType: async (accountType: AccountType) => {
    return await apiClient.patch<UserProfile>('/users/account-type', { accountType });
  },

  /**
   * Check username availability
   * GET /users/username/check?username=<username>
   */
  checkUsernameAvailability: async (username: string) => {
    return await apiClient.get<{ available: boolean; message: string }>(`/users/username/check?username=${encodeURIComponent(username)}`);
  },

  /**
   * Update username
   * PATCH /users/me/username
   */
  updateUsername: async (username: string) => {
    return await apiClient.patch<UserProfile>('/users/me/username', { username });
  },

  /**
   * Search users for @ mentions
   * GET /users/search?q=<query>
   * If query is empty, returns popular/recent users
   */
  searchUsers: async (query: string): Promise<UserProfile[]> => {
    try {
      console.log('🔍 API: Searching users with query:', query);
      const endpoint = query.trim() 
        ? `/users/search?q=${encodeURIComponent(query)}`
        : '/users/search'; // Empty query for popular users
      
      const response = await apiClient.get<UserProfile[]>(endpoint);
      console.log('✅ API Response:', response);
      
      if (response.success && response.data) {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error('❌ Error searching users:', error);
      return [];
    }
  },
};
